import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Avatar,
  IconButton,
  Stack,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import {
  PayPalButtons,
  PayPalCardFieldsForm,
  PayPalCardFieldsProvider,
  PayPalScriptProvider,
  usePayPalCardFields,
} from "@paypal/react-paypal-js";
import apiService from "../../../../../../service/Api";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../../components/common/ShowToast";
import EftpayPaymentForm from "../chema/EftpayPaymentForm";
import ZimswitchPaymentForm from "../chema/ZimswitchPaymentForm";
import { validatePhone } from "../chema/validatePhone";
import { slugFromStreamTitle } from "../chema/slug";
import {
  capturePayPalChemaDonation,
  initiatePayPalChemaDonation,
} from "../chema/donationsApi";
import ecocashLogo from "../../../../../../assets/ecocash.png";
import paynowLogo from "../../../../../../assets/paynow.svg";
import zimswitchLogo from "../../../../../../assets/zimswitch.png";
import visaLogo from "../../../../../../assets/visa.jpg";
import paypalLogo from "../../../../../../assets/paypal.svg";
import { isDebugEnvironment } from "../../../../../../utils/env";

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || "";

type PayVia =
  | "ecocash_paynow"
  | "ecocash_nmb"
  | "paynow"
  | "zimswitch"
  | "card"
  | "paypal";

const PAYMENT_METHODS: { id: PayVia; label: string; logo: string }[] = [
  { id: "ecocash_paynow", label: "EcoCash (PayNow)", logo: ecocashLogo },
  { id: "ecocash_nmb", label: "EcoCash (NMB)", logo: ecocashLogo },
  { id: "paynow", label: "Paynow", logo: paynowLogo },
  { id: "zimswitch", label: "ZimSwitch", logo: zimswitchLogo },
  { id: "card", label: "Visa / Mastercard", logo: visaLogo },
  { id: "paypal", label: "PayPal", logo: paypalLogo },
];

function PayCardButton({
  disabled,
  onError,
}: {
  disabled: boolean;
  onError: (msg: string) => void;
}) {
  const { cardFieldsForm } = usePayPalCardFields();
  const [busy, setBusy] = useState(false);

  const handleClick = async () => {
    if (!cardFieldsForm) {
      onError("Card form is still loading — please wait a moment and try again.");
      return;
    }
    setBusy(true);
    try {
      const state = await cardFieldsForm.getState();
      if (!state.isFormValid) {
        onError("Please fill in all card details correctly.");
        setBusy(false);
        return;
      }
      await cardFieldsForm.submit();
    } catch (e) {
      onError((e as Error).message || "Card payment failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      type="button"
      fullWidth
      variant="contained"
      onClick={handleClick}
      disabled={disabled || busy}
      sx={{
        mt: 1.5,
        borderRadius: "999px",
        backgroundColor: "#111827",
        "&:hover": { backgroundColor: "#000" },
      }}
    >
      {busy ? "Processing..." : "Pay with card"}
    </Button>
  );
}

/**
 * PayPal Smart Buttons + hosted card fields for the chema donation dialog.
 * PayPal settles in USD, so the amount is charged in USD regardless of the
 * dialog's display currency.
 */
export function ChemaPayPalSection({
  amount,
  donorName,
  isAnonymous,
  streamId,
  onPaid,
}: {
  amount: number | null;
  donorName: string;
  isAnonymous: boolean;
  streamId: number | null;
  onPaid: (donationId?: number) => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [donationId, setDonationId] = useState<number | null>(null);
  const numeric = amount && amount > 0 ? amount : null;

  if (!PAYPAL_CLIENT_ID) {
    return (
      <Alert severity="warning">
        PayPal is not configured. Set REACT_APP_PAYPAL_CLIENT_ID in the frontend
        .env to enable it.
      </Alert>
    );
  }

  const createOrder = async (): Promise<string> => {
    setError(null);
    setInfo(null);
    if (!numeric) throw new Error("Please enter a valid donation amount.");
    if (!streamId) throw new Error("Stream information is missing.");
    const res = await initiatePayPalChemaDonation({
      amount: numeric,
      donor_name: isAnonymous ? undefined : donorName.trim() || undefined,
      is_anonymous: isAnonymous,
      stream_id: streamId,
    });
    setDonationId(res.donation_id);
    return res.order_id;
  };

  const onApprove = async () => {
    if (!donationId) return;
    try {
      const r = await capturePayPalChemaDonation(donationId);
      if (r.success) {
        setInfo("Payment confirmed. Thank you for your donation!");
        onPaid(donationId);
      } else {
        setError(`Payment status: ${r.status}`);
      }
    } catch (e) {
      setError((e as Error).message || "Capture failed.");
    }
  };

  const errMessage = (e: unknown) =>
    typeof (e as { message?: unknown })?.message === "string"
      ? (e as { message: string }).message
      : "PayPal error";

  return (
    <Box>
      {info && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {info}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Pay with your PayPal balance, or a card (Visa, Mastercard, Amex). PayPal
        is charged in USD.
      </Typography>

      <PayPalScriptProvider
        options={{
          clientId: PAYPAL_CLIENT_ID,
          currency: "USD",
          components: "buttons,card-fields",
          "enable-funding": "card",
        }}
      >
        <PayPalButtons
          style={{ layout: "vertical", label: "donate" }}
          disabled={!numeric}
          createOrder={createOrder}
          onApprove={onApprove}
          onError={(e) => setError(errMessage(e))}
        />

        <Box
          sx={{
            my: 2,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            color: "text.secondary",
            fontSize: 12,
          }}
        >
          <Divider sx={{ flex: 1 }} />
          <span>or pay with card</span>
          <Divider sx={{ flex: 1 }} />
        </Box>

        <PayPalCardFieldsProvider
          createOrder={createOrder}
          onApprove={onApprove}
          onError={(e: unknown) => setError(errMessage(e))}
          style={{
            input: {
              "font-size": "14px",
              color: "#111827",
              padding: "10px 12px",
            },
            ".invalid": { color: "#dc2626" },
          }}
        >
          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1.5,
              p: 1.5,
            }}
          >
            <PayPalCardFieldsForm />
          </Box>
          <PayCardButton disabled={!numeric} onError={setError} />
        </PayPalCardFieldsProvider>
      </PayPalScriptProvider>
    </Box>
  );
}

export interface ChemaDonationDialogProps {
  open: boolean;
  onClose: () => void;
  streamId: number | null;
  streamTitle?: string;
  deceasedImage?: string;
  onDonationSuccess?: (donationId?: number) => void;
}

const isLocal = isDebugEnvironment();

export default function ChemaDonationDialog({
  open,
  onClose,
  streamId,
  streamTitle,
  deceasedImage,
  onDonationSuccess,
}: ChemaDonationDialogProps) {
  const [selectedAmount, setSelectedAmount] = useState<number | null>(
    isLocal ? 0.01 : 10,
  );
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [donorName, setDonorName] = useState("");
  const [email, setEmail] = useState("");
  const [memory, setMemory] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [payVia, setPayVia] = useState<PayVia>("ecocash_paynow");
  const [step, setStep] = useState<"details" | "method">("details");
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<string | null>(null);
  const [pollMessage, setPollMessage] = useState("");

  const routeParams = useParams<{ pageType?: string }>();
  const donationPageType = routeParams.pageType || "memorial-page";
  const donationFullName = slugFromStreamTitle(streamTitle);

  const handlePhoneChange = (value: string) => {
    const cleaned = value.replace(/[^0-9+]/g, "");
    setPhone(cleaned);
    if (phoneError) setPhoneError(validatePhone(cleaned));
  };

  const handleClose = () => {
    if (isProcessing) return;
    setPaymentStatus(null);
    setPollMessage("");
    setPhone("");
    setPhoneError("");
    setDonorName("");
    setEmail("");
    setIsAnonymous(false);
    setMemory("");
    setPayVia("ecocash_paynow");
    setStep("details");
    setSelectedAmount(isLocal ? 0.01 : 10);
    onClose();
  };

  const handlePayPalPaid = (donationId?: number) => {
    setPaymentStatus("paid");
    showSuccessToast("Payment received! Thank you for your donation.");
    onDonationSuccess?.(donationId);
    setTimeout(() => handleClose(), 3000);
  };

  const validateForEftpay = (): boolean => {
    if (!selectedAmount || selectedAmount <= 0) {
      showErrorToast("Please enter a donation amount greater than zero");
      return false;
    }
    if (!streamId) {
      showErrorToast("Stream information is missing. Please refresh and try again.");
      return false;
    }
    return true;
  };

  const pollPaymentStatus = async (donationId: number) => {
    const maxAttempts = 60;
    let attempts = 0;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setPaymentStatus("timeout");
        setIsProcessing(false);
        setPollMessage("Payment timed out. Please check your phone and try again.");
        showErrorToast("Payment timed out. Please check your phone and try again.");
        return;
      }
      attempts++;
      try {
        const res = await apiService.get<{
          paid: boolean;
          status: string;
          message: string;
          donation_id: number;
        }>(`/api/chema/donations/${donationId}/status/`);

        const { paid, status: paymentStat, message } = res.data || {};

        if (paid) {
          setPaymentStatus("paid");
          setIsProcessing(false);
          setPollMessage(message || "Payment confirmed. Thank you for your donation!");
          showSuccessToast(message || "Payment received! Thank you for your donation.");
          onDonationSuccess?.(donationId);
          setTimeout(() => handleClose(), 3000);
          return;
        }

        const currentStatus = paymentStat || "pending";
        setPaymentStatus(currentStatus);
        setPollMessage(message || "Checking payment status...");

        if (currentStatus === "cancelled" || currentStatus === "failed") {
          setIsProcessing(false);
          showErrorToast(message || `Payment ${currentStatus}. Please try again.`);
          return;
        }

        setTimeout(poll, 5000);
      } catch {
        setTimeout(poll, 5000);
      }
    };

    poll();
  };

  const handleProceedWithPayment = async () => {
    if (!selectedAmount || selectedAmount <= 0) {
      showErrorToast("Please enter a donation amount greater than zero");
      return;
    }
    const phoneValidation = validatePhone(phone);
    if (phoneValidation) {
      setPhoneError(phoneValidation);
      showErrorToast(phoneValidation);
      return;
    }
    if (!streamId) {
      showErrorToast("Stream information is missing. Please refresh and try again.");
      return;
    }

    setIsProcessing(true);
    setPaymentStatus("sending");

    try {
      const response = await apiService.post<{
        success: boolean;
        donation_id: number;
        message: string;
      }>("/api/chema/donations/initiate/mobile/", {
        amount: selectedAmount,
        stream_id: streamId,
        currency,
        phone,
        method: "ecocash",
        email: email.trim() || undefined,
        message: memory.trim() || undefined,
        donor_name: isAnonymous ? "" : donorName,
        is_anonymous: isAnonymous,
      });

      if (response.data?.success) {
        setPaymentStatus("awaiting_approval");
        showSuccessToast(response.data.message || "Check your phone to approve the payment.");
        pollPaymentStatus(response.data.donation_id);
      } else {
        showErrorToast("Payment initialization failed. Please try again.");
        setIsProcessing(false);
        setPaymentStatus(null);
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Object &&
        "response" in error &&
        typeof error.response === "object" &&
        error.response !== null &&
        "data" in error.response &&
        typeof (error.response as Record<string, unknown>).data === "object" &&
        (error.response as Record<string, unknown>).data !== null &&
        "error" in ((error.response as Record<string, unknown>).data as Record<string, unknown>)
          ? (((error.response as Record<string, unknown>).data as Record<string, unknown>).error as string)
          : "Failed to initiate payment. Please try again.";
      showErrorToast(errorMessage);
      setIsProcessing(false);
      setPaymentStatus(null);
    }
  };

  // Paynow web checkout: create the donation server-side and hand off to the
  // hosted Paynow page.
  const handleProceedWithPaynow = async () => {
    if (!selectedAmount || selectedAmount <= 0) {
      showErrorToast("Please enter a donation amount greater than zero");
      return;
    }
    if (!streamId) {
      showErrorToast("Stream information is missing. Please refresh and try again.");
      return;
    }

    setIsProcessing(true);
    try {
      const response = await apiService.post<{
        success: boolean;
        redirect_url: string;
        donation_id: number;
      }>("/api/chema/donations/initiate/", {
        amount: selectedAmount,
        stream_id: streamId,
        currency,
        message: memory,
        donor_name: isAnonymous ? "" : donorName,
        is_anonymous: isAnonymous,
      });

      if (response.data?.success && response.data.redirect_url) {
        window.location.href = response.data.redirect_url;
      } else {
        showErrorToast("Payment initialization failed. Please try again.");
        setIsProcessing(false);
      }
    } catch {
      showErrorToast("Failed to initiate payment. Please try again.");
      setIsProcessing(false);
    }
  };

  const handleProceedToMethod = () => {
    if (!selectedAmount || selectedAmount <= 0) {
      showErrorToast("Please enter a donation amount greater than zero");
      return;
    }
    setStep("method");
  };

  const familyName = streamTitle?.replace("Stream for ", "") || "The";

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar src={deceasedImage || ""} sx={{ width: 56, height: 56 }}>
              {streamTitle ? streamTitle.charAt(0) : "C"}
            </Avatar>
            <Box>
              <Typography variant="subtitle2">
                Your contribution helps{" "}
                <Typography component="span" fontWeight="bold">
                  {familyName} Family
                </Typography>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Helping today builds stronger ties for tomorrow.
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={handleClose} size="small" sx={{ ml: "auto" }}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        {step === "method" && (
          <Typography
            variant="body2"
            onClick={() => !isProcessing && setStep("details")}
            sx={{ mb: 2, fontWeight: 700, cursor: isProcessing ? "default" : "pointer" }}
          >
            &larr; Back
          </Typography>
        )}

        {step === "details" && (
        <>
        <Typography variant="body2" sx={{ mt: 2, mb: 1, fontWeight: 500 }}>
          Enter Your Donation
        </Typography>

        <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Donation Amount"
            value={selectedAmount ?? ""}
            onChange={(e) => {
              const v = e.target.value.replace(/[^0-9.]/g, "");
              setSelectedAmount(v.length > 0 ? parseFloat(v) : null);
            }}
            fullWidth
            disabled={isProcessing}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
          />
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel id="dlg-currency-label">Select Currency</InputLabel>
            <Select
              labelId="dlg-currency-label"
              value={currency}
              label="Select Currency"
              onChange={(e) => setCurrency(e.target.value)}
              disabled={isProcessing}
            >
              <MenuItem value="USD">USD</MenuItem>
              <MenuItem value="ZWL">ZWL</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        <Stack direction="row" spacing={1} justifyContent="space-between" mt={3} flexWrap="wrap">
          {(isLocal ? [0.01, 0.1, 0.5, 1, 5, 10] : [10, 20, 30, 50, 60]).map((amount) => (
            <Box key={amount} sx={{ position: "relative", flex: "1 1 80px", m: 0.5 }}>
              <Button
                fullWidth
                variant={selectedAmount === amount ? "contained" : "outlined"}
                onClick={() => setSelectedAmount(amount)}
                disabled={isProcessing}
                sx={{ borderRadius: 2, textTransform: "none" }}
              >
                ${amount}
              </Button>
              {amount === 30 && (
                <Typography
                  sx={{
                    textAlign: "center",
                    mt: 0.5,
                    fontSize: "0.6rem",
                    fontWeight: 700,
                    color: "primary.main",
                    bgcolor: "rgba(21, 95, 82, 0.1)",
                    borderRadius: "8px",
                    py: 0.2,
                  }}
                >
                  SUGGESTED
                </Typography>
              )}
            </Box>
          ))}
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Your Details</Typography>

        <FormControlLabel
          control={
            <Checkbox
              checked={isAnonymous}
              onChange={(e) => { setIsAnonymous(e.target.checked); if (e.target.checked) setDonorName(""); }}
              disabled={isProcessing}
            />
          }
          label={<Typography variant="body2">Donate anonymously</Typography>}
        />

        {!isAnonymous && (
          <TextField
            label="Your Name"
            placeholder="Enter your name"
            value={donorName}
            onChange={(e) => setDonorName(e.target.value)}
            fullWidth
            disabled={isProcessing}
            sx={{ mt: 1 }}
          />
        )}

        <TextField
          label="Email address"
          placeholder="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          disabled={isProcessing}
          sx={{ mt: 1.5 }}
        />

        <TextField
          label={`Leave a memory for ${streamTitle || "the deceased"}`}
          multiline
          minRows={3}
          fullWidth
          value={memory}
          onChange={(e) => setMemory(e.target.value)}
          sx={{ mt: 2 }}
          disabled={isProcessing}
        />
        </>
        )}

        {step === "method" && (
        <>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
          Payment Method
        </Typography>

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          {PAYMENT_METHODS.map((m) => (
            <Box
              key={m.id}
              onClick={() => !isProcessing && setPayVia(m.id)}
              sx={{
                position: "relative",
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
                py: 1.5,
                borderRadius: 2,
                border: "2px solid",
                borderColor: payVia === m.id ? "primary.main" : "divider",
                cursor: isProcessing ? "default" : "pointer",
                opacity: isProcessing ? 0.6 : 1,
                transition: "border-color 0.15s",
                "&:hover": {
                  borderColor: isProcessing ? "divider" : "primary.light",
                },
              }}
            >
              {m.id === "ecocash_nmb" && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 2,
                    right: 2,
                    fontSize: "0.55rem",
                    fontWeight: 700,
                    color: "#fff",
                    bgcolor: "#0e463c",
                    borderRadius: "6px",
                    px: 0.5,
                    lineHeight: 1.4,
                  }}
                >
                  NMB
                </Box>
              )}
              <Box
                component="img"
                src={m.logo}
                alt={m.label}
                sx={{
                  height: m.id === "zimswitch" ? 36 : 26,
                  maxWidth: m.id === "zimswitch" ? 80 : 56,
                  objectFit: "contain",
                }}
              />
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {m.label}
              </Typography>
            </Box>
          ))}
        </Stack>

        {payVia === "ecocash_paynow" ? (
          <>
            <TextField
              label="Phone Number"
              placeholder="e.g. 0771234567"
              value={phone}
              onChange={(e) => handlePhoneChange(e.target.value)}
              onBlur={() => setPhoneError(validatePhone(phone))}
              error={!!phoneError}
              helperText={phoneError || "Enter your EcoCash number"}
              fullWidth
              disabled={isProcessing}
            />

            {paymentStatus &&
              !["paid", "cancelled", "failed", "timeout"].includes(paymentStatus) && (
                <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: "info.light", color: "info.contrastText", textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">{pollMessage || "Waiting for payment approval on your phone..."}</Typography>
                </Box>
              )}

            {paymentStatus === "paid" && (
              <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: "success.light", color: "success.contrastText", textAlign: "center" }}>
                <Typography variant="body2" fontWeight="bold">{pollMessage || "Payment received! Thank you for your donation."}</Typography>
              </Box>
            )}

            {["cancelled", "failed", "timeout"].includes(paymentStatus ?? "") && (
              <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: "error.light", color: "error.contrastText", textAlign: "center" }}>
                <Typography variant="body2" fontWeight="bold">{pollMessage || "Payment was not completed. Please try again."}</Typography>
              </Box>
            )}
          </>
        ) : payVia === "ecocash_nmb" ? (
          <EftpayPaymentForm
            channel="ecocash"
            amount={selectedAmount}
            currency={currency}
            email={email}
            streamId={streamId}
            pageType={donationPageType}
            fullName={donationFullName}
            onValidate={validateForEftpay}
          />
        ) : payVia === "paynow" ? (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            You&apos;ll be securely redirected to Paynow to complete your donation.
          </Typography>
        ) : payVia === "zimswitch" ? (
          <ZimswitchPaymentForm
            amount={selectedAmount}
            currency={currency}
            email={email}
            streamId={streamId}
            pageType={donationPageType}
            fullName={donationFullName}
            onValidate={validateForEftpay}
          />
        ) : payVia === "card" ? (
          <EftpayPaymentForm
            channel="card"
            amount={selectedAmount}
            currency="USD"
            email={email}
            streamId={streamId}
            pageType={donationPageType}
            fullName={donationFullName}
            onValidate={validateForEftpay}
          />
        ) : (
          <ChemaPayPalSection
            amount={selectedAmount}
            donorName={donorName}
            isAnonymous={isAnonymous}
            streamId={streamId}
            onPaid={handlePayPalPaid}
          />
        )}
        </>
        )}
      </DialogContent>

      {step === "details" && (
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleProceedToMethod}
            variant="contained"
            color="primary"
            fullWidth
            disabled={isProcessing}
            sx={{ borderRadius: "30px", textTransform: "none", py: 1.2 }}
          >
            Proceed to Pay
          </Button>
        </DialogActions>
      )}

      {step === "method" && (payVia === "ecocash_paynow" || payVia === "paynow") && (
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={
              payVia === "paynow"
                ? handleProceedWithPaynow
                : handleProceedWithPayment
            }
            variant="contained"
            color="primary"
            fullWidth
            disabled={isProcessing}
            sx={{ borderRadius: "30px", textTransform: "none", py: 1.2 }}
          >
            {isProcessing ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: "#fff" }} />
                {paymentStatus === "awaiting_approval" ? "Waiting for approval..." : "Processing..."}
              </>
            ) : paymentStatus === "paid" ? (
              "Payment Complete"
            ) : payVia === "paynow" ? (
              "Proceed to Paynow"
            ) : (
              "Pay via EcoCash"
            )}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
